// New Agents training module for RESIDENCE | eXp Realty.
// Content comes from Kelly's Wix course. Only steps with finished content are included.
// Not yet included: Basic MLS search step text, Listing Contract, Time Management & Organization,
// Your Weekly New Agent Action Plan, FAST Cap Program, Now What?

export const newAgentsModule = {
  id: 'newAgents',
  title: 'Basic Skills',
  intro:
    'Welcome to the New Agents module. Work through each step in order. Your progress is saved to your account.',
  sections: [
    {
      id: 'mls-skills',
      title: 'Basic MLS Skills, Navigation & Home Valuations',
      steps: [
        {
          id: 'mls-basic-search',
          title: 'How to Search for Homes on the MLS',
          body: [],
          links: [
            { label: 'Watch: How to Search for Homes on the MLS', url: 'https://youtu.be/s3rg6QLz4sE' },
          ],
        },
        {
          id: 'nabor-mls-tutorials',
          title: 'NABOR MLS Tutorial Videos',
          body: [
            'NABOR has a library of free, short MLS tutorial videos that walk you through everyday tasks in Matrix. Use them any time you want a quick refresher.',
            'How to find them:\n1. Sign in to Matrix.\n2. Click on NABOR.\n3. Go to the Links section and open the NABOR MLS Resources page.\nYou can also open the page directly with the link below.',
            'Videos include: creating auto emails, adding or finding a selling agent for pending listings, adding photos to a listing, scheduling an open house, searching for open houses, adding fields to a search, the difference between expired and terminated listings, Fill From Cross Property, and adding your photo to the MLS roster.',
            'The same page also has the MLS Rules and the MLS training guide handouts.',
          ],
          images: [
            { src: '/training/nabor-mls-resources.png', alt: 'NABOR MLS Resources page showing the MLS Tutorial Videos', label: 'The MLS Tutorial Videos section of the NABOR MLS Resources page', wide: true },
          ],
          links: [
            { label: 'NABOR MLS Resources page', url: 'https://www.nabor.com/realtor-tools/mls-resources' },
          ],
        },
        {
          id: 'mls-advanced',
          title: 'MLS Search, Search Alerts, Property Stats and More',
          body: [
            'The video below will walk you through next-level use of the MLS, including setting up property alerts, carts, detailed searches, researching property sales and statistics, and more!',
          ],
          links: [
            { label: 'Watch: MLS Search, Search Alerts, Property Stats and More', url: 'https://youtu.be/Ml6DZOwVkDI' },
          ],
        },
        {
          id: 'cma',
          title: 'How to Create a Comparative Market Analysis',
          body: [
            'Create a Comparative Market Analysis (CMA):',
            '1.\nStart with the MLS.\nLook up the property address in the MLS to see if it has been listed before.\nIf it has, use that information to begin your CMA.\nIf the home has never been listed, or you cannot find it in the MLS, use the property appraiser\'s website to gather basic details like square footage, bedrooms, bathrooms, pool, and lot type (for example, lake or preserve view).',
            '2. Build the CMA. Use the MLS tool to compare the property with others in the area. In cookie-cutter communities, compare properties with the same floor plan. In less uniform neighborhoods, focus on key differences like pools, views, and upgrades.',
            '3.\nAdjust for Features.\nUse advanced CMA adjustments in the MLS to account for key differences.\nThe amounts below are examples only, not fixed values.\nValues change with the market, so use current local sales to set each adjustment.\nFor example, a pool might be $80,000 for a basic pool, $100,000 if it includes a spa, up to $150,000 for custom pools.\nLake View: $50,000 value over a standard lot.\nPreserve View: $25,000 value over a standard lot.\nInferior Lots: subtract $10,000 if a lot backs directly into the side of another home.',
            '4. Evaluate Upgrades. Read descriptions of comparable active, pending, and sold homes for upgrades like hurricane windows, outdoor kitchens, or custom closets. Assume the subject property has none of these and adjust accordingly.',
            '5. Print the CMA. Bring the CMA to your appointment and note that it is based on online data. During the appointment, update it with any upgrades the homeowner mentions.',
          ],
          links: [
            { label: 'Watch: How to Create a Comparative Market Analysis', url: 'https://youtu.be/gh88If6djU4' },
          ],
        },
      ],
    },
    {
      id: 'contracts',
      title: 'Contracts',
      steps: [
        {
          id: 'far-vs-nabor',
          title: 'FAR vs NABOR Contracts',
          body: [
            'Watch the video to learn the differences between the FAR and NABOR contracts. Download the examples below.',
            'Please note these examples are for resale single-family homes. There are also different versions of these contracts if you are buying a condo or vacant land, and the big builders have their own sets of contracts that they use.',
          ],
          links: [
            { label: 'Watch: FAR vs NABOR Contracts', url: 'https://youtu.be/OBQyqDphtKY' },
            { label: 'FAR vs NABOR Spreadsheet', url: 'https://docs.google.com/spreadsheets/d/105Temtt0c7wbTFlDGbffEysQMc5S__Qr/edit' },
            { label: 'Standard FAR Purchase Contract', url: 'https://drive.google.com/file/d/1ipQhSi-EPFtO6MyZMVf0dGzYli7DyyvP/view?usp=sharing' },
            { label: 'Standard NABOR Purchase Contract', url: 'https://drive.google.com/file/d/1bYqK4rbJDB6jnJQEXFX4UEG2ERavYp4o/view' },
          ],
        },
        {
          id: 'standard-vs-as-is',
          title: 'Standard vs As-Is Contracts',
          body: [
            'Watch the video to learn the differences between standard and as-is contracts. Download the examples below.',
          ],
          links: [
            { label: 'Watch: Standard vs As-Is Contracts', url: 'https://youtu.be/tyerkc8nPCk' },
            { label: 'Standard FAR Purchase Contract', url: 'https://drive.google.com/file/d/1ipQhSi-EPFtO6MyZMVf0dGzYli7DyyvP/view?usp=sharing' },
            { label: 'As-Is FAR Purchase Contract', url: 'https://drive.google.com/file/d/1ZjfiBgJiDWQF3_etloD90oa-MG7OLbP-/view?usp=sharing' },
          ],
        },
        {
          id: 'writing-an-offer',
          title: 'Writing an Offer',
          body: [
            'Writing the Offer: watch the step-by-step tutorial below.',
            'Contract Must-Haves:',
            '- Wiring Fraud (eXp Florida Library)',
            '- Affiliated Business Disclosure (ABA) (eXp Florida Library)',
            '- Flood Disclosure (from the seller)',
            '- Seller to Buyers Broker Commission Agreement (FAR Libraries)',
            '- Mold/Radon/Drywall Addendum for all Standard FAR contracts. IMPORTANT: In Item 19 of the contract, do NOT check the boxes for Mold and Drywall Addendum. Instead, check the "other" box and type in the name of the Mold/Radon/Drywall Addendum.',
            '- Include a pre-approval or proof of funds letter with the offer email.',
            'Contract Options:',
            '- Deposit amount: can be paid in 2 installments.',
            '- Escalation clause: use it if there is a lot of activity or other offers.',
            '- Contingencies: inspection, mortgage, sale of home (somewhat unusual).',
            '- Sellers Disclosures: see MLS Supplements.',
            '- Personal Interest.',
            '- Condo and HOA: see MLS Supplements.',
            '- CDD.',
            'After sending the contract to the client for signatures, call the client to review the terms and make sure they understand and agree.',
            'Counteroffers:',
            '- Upload the full counteroffer into the client\'s transaction file in Resi. Use the signature tools to strike information or add a text box. Have the client initial and date next to any change.',
            '- If there is no space, write a new offer.',
            '- Follow the same email procedure as above.',
          ],
          links: [
            { label: 'Watch: Offer Step by Step Tutorial', url: 'https://us02web.zoom.us/rec/play/BjFi4ZZ3qRAHs-7bEvz-GM1oC8fPWbdvFMxsWRw4xjcMMY-82okViFJgnYQCPHWHYV0747Xcz-22Ecsm.ovxeMx27oPwWypB3' },
            { label: 'Mold/Radon/Drywall Addendum (for all Standard FAR contracts)', url: 'https://drive.google.com/file/d/1XZ5bjSFeJuHmbq0q3rN4MIxoiZ_mxYyq/view?usp=drivesdk' },
          ],
        },
        {
          id: 'listing-agreements',
          title: 'Listing Agreements',
          body: [
            'Watch the video below to learn how to use listing agreements.',
          ],
          links: [
            { label: 'Watch: Contracts Part 2: Listing Agreements', url: 'https://youtu.be/4niCZiSGRuw' },
          ],
        },
      ],
    },
    {
      id: 'time-management',
      title: 'Time Management & Organization',
      steps: [
        {
          id: 'google-drive',
          title: 'Google Drive: Your Cloud Storage System',
          body: [
            'As a busy agent, you will need documents at your fingertips whether you are in front of a computer or on your mobile. Watch this tutorial on Google Drive so you know how to access documents and share them when needed.',
          ],
          links: [
            { label: 'Watch: How to use Google Drive', url: 'https://www.youtube.com/watch?v=G5B6ksOKlYI' },
          ],
        },
        {
          id: 'time-blocking',
          title: 'Time Blocking with Google Calendar',
          body: [],
          links: [
            { label: 'Watch: Time Blocking with Google Calendar', url: 'https://youtu.be/m3M9G4K41h8' },
          ],
        },
        {
          id: 'organizing-gmail',
          title: 'Organizing Your Gmail for Efficiency',
          body: [],
          links: [
            { label: 'Watch: Organizing Your Gmail for Efficiency', url: 'https://youtu.be/oLdHnWLbn4A' },
          ],
        },
        {
          id: 'google-calendar',
          title: 'Google Calendar',
          body: [
            'We could not survive without our Google Calendars! Learning to use your Google Calendar for your business and personal life is critical to your success as an agent, and to maintaining a work-life balance. Do NOT overlook this step! Watch the video below to begin your journey.',
          ],
          links: [
            { label: 'Watch: Google Calendar', url: 'https://youtu.be/aZNvRtQYsns' },
          ],
        },
        {
          id: 'resi-google-calendar-setup',
          title: 'Connect Resi to Your Google Calendar',
          body: [
            'Resi can add your appointments to your Google Calendar automatically, and changes you make in Google come back to Resi. You only connect once, and it works on your phone and your computer.',
            'On a computer: go to My Profile, find Connected accounts, and click Connect Google. Pick your Google account. If Google says "Google hasn\'t verified this app," click Advanced, then Go to Resi. Tick the Google Calendar box and click Continue.',
            'On an iPhone: open Resi in Safari, go to My Profile, and tap Connect Google. Resi takes you to Google and brings you back when you are done. If it says popup blocked, go to Settings > Safari, turn off Block Pop-ups, then tap Connect Google again. Or just connect from a computer; the connection works on both.',
            'Once connected, the Profile card says Connected with your Google email. Appointments can take up to about 15 minutes to appear in Google Calendar. To push them right away, tap "Sync now" on your Profile.',
          ],
          links: [],
        },
        {
          id: 'advanced-gmail',
          title: 'Advanced Gmail Tips and Tricks',
          body: [],
          links: [
            { label: 'Watch: Advanced Gmail Tips and Tricks', url: 'https://youtu.be/g6zYF7fbJk8' },
          ],
        },
      ],
    },
    {
      id: 'bold-trail',
      title: 'Bold Trail Website and CRM',
      steps: [
        {
          id: 'bold-trail-intro',
          title: 'Introduction to Bold Trail',
          body: [
            'Bold Trail is our CRM and website.',
            'Please Note: If you are outside the SWFL MLS, you do NOT want to join the local RESIDENCE | eXp Realty Team for Bold Trail.',
          ],
          links: [
            { label: 'Watch: Introduction to Bold Trail', url: 'https://drive.google.com/file/d/1QNIdbNlM5wjL81EG1Jc415YhXesBjdjo/view?usp=sharing' },
          ],
        },
      ],
    },
    {
      id: 'skyslope',
      title: 'Skyslope Transaction Management',
      steps: [
        {
          id: 'skyslope-intro',
          title: 'Introduction to Skyslope',
          body: [
            'Skyslope is our transaction management system, with forms and DigiSign for sending documents to clients for signature.',
            'In the next step, you will see how to create a file and add forms to send to a client for signing right away.',
          ],
          links: [
            { label: 'Watch: Skyslope Basics', url: 'https://youtu.be/i0R47KV-hFE' },
          ],
        },
        {
          id: 'skyslope-send-documents',
          title: 'How to Send Documents to Clients for Signing',
          body: [],
          links: [
            { label: 'Watch: How to Send Documents to Clients for Signing', url: 'https://youtu.be/7dTamodLKJk' },
          ],
        },
      ],
    },
  ],
};

// Sections parked for later (not in Basic Skills yet). Kept so no content is lost.
const parkedSections = [
    {
      id: 'fast-cap',
      title: 'FAST Cap Program',
      steps: [
        {
          id: 'fast-cap-program',
          title: 'About FastCAP',
          body: [
            'FastCAP is a 6-week training program for new agents, run by eXp Corporate and offered every couple of months. This program is 100% PURE GOLD!',
            'This is NOT just education. It is TRAINING, with 3 hours of online instruction each week plus a few hours of homework each day to IMPLEMENT what you have learned.',
            'Over six game-changing weeks, you will be guided through FastCAP, revealing the proven strategies that top FastCAP agents use, plus their own insider secrets that skyrocketed their success.\nYou will learn from eXp agents who created truly amazing results in their first year, build relationships, and form habits for immediate success.\nActualize intentions, customize systems, and uncap your potential.',
            'In just six weeks, agents who complete FastCAP will walk away with refined business systems, strategies and habits of successful agents, and a growing pipeline of business. They will show you how to CAP, and how to dominate your market and scale your business faster than ever.',
            'You have 2 options for FastCAP, and you decide how to go through it based on how you learn and how much accountability you need. This is YOUR BUSINESS and EVERYTHING is at stake for your success. Make the right choice for YOU!',
            'Pre-Recorded 6-Week Session: A previous FastCAP 6-week course is recorded and available on this site.\nThe assignments and action items have been fine-tuned to the most realistic activities for new agents, with more how-to video tutorials layered in to help you every step of the way.\nTo get started right away, click your profile icon in the upper right corner of this site, select MY PROGRAMS, and select FastCAP - Self Paced.',
            'LIVE 6-Week Session: Visit eXp University to check out the next dates, learn more, and register.',
          ],
          links: [
            { label: 'eXp University: FastCAP Live Dates and Registration', url: 'https://www.expuniversity.com/fastcap' },
          ],
        },
      ],
    },
    {
      id: 'now-what',
      title: 'Now What?',
      steps: [
        {
          id: 'now-what-fastcap',
          title: 'Now What? Next Steps',
          body: [
            'FastCAP is a 6-week training program for new agents, run by eXp Corporate and offered every couple of months.',
            'This is NOT just education. It is TRAINING, with 3 hours of live online instruction each week plus a few hours of homework each day to IMPLEMENT what you have learned.',
            'Over six game-changing weeks, you will be guided through FastCAP, revealing the proven strategies that top FastCAP agents use, plus their own insider secrets that skyrocketed their success.',
            'In this six-week immersive journey, you will learn from eXp agents who created truly amazing results in their first year, build relationships, and form habits for immediate success. Actualize intentions, customize systems, and uncap your potential.',
            'In just six weeks, agents who complete FastCAP will walk away with refined business systems, strategies and habits of successful agents, and a growing pipeline of business. They won\'t just show you how to CAP. They\'ll show you how to dominate your market and scale your business faster than ever.',
            'Go to eXp University to check out the next dates, learn more, and get registered.',
          ],
          links: [
            { label: 'eXp University: FastCAP Live Dates and Registration', url: 'https://www.expuniversity.com/fastcap' },
          ],
        },
      ],
    },
];

export { parkedSections };

export const allNewAgentSteps = newAgentsModule.sections.flatMap((section) =>
  section.steps.map((step) => ({ ...step, sectionId: section.id, sectionTitle: section.title }))
);
